import { guardGameSurface } from './186-game-surface.mjs';
import { createDragSurface as createGameDragSurface } from './186-drag-surface.mjs';
import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { attachClusterInput } from './210-cluster-input.mjs';

const brushes = new Set(['mosaic-paint', 'pattern-stamp', 'cloud-shapes', 'paper-fold']);
const directGames = new Set([
  'ball-sort',
  'gem-swap',
  'soft-merge',
  'pebble-balance',
  'tile-slide',
]);
const directions = { ArrowLeft: 0, ArrowRight: 1, ArrowUp: 2, ArrowDown: 3 };

/** Optional gestures supplement native buttons. Ordinary cells retain vertical
 * scrolling; Ribbon Run endpoints explicitly own all-axis tracing. Read live
 * state before each action and release capture/listeners with the owning card. */
export function attachGameInput(board, readState, move) {
  if (readState().gameId === 'cluster-pop') return attachClusterInput(board, readState);
  let gesture = null;
  let suppressClick = false;
  const listeners = [];
  const returns = new Set();
  const listen = (name, handler, options) => {
    board.addEventListener(name, handler, options);
    listeners.push([name, handler, options]);
  };
  const indexAt = (event) => {
    const target = event.target?.closest?.('[data-cell]');
    return target && board.contains(target) ? Number(target.dataset.cell) : -1;
  };
  const applyCell = (index) => {
    const state = readState();
    const cell = gameView(state).cells[index];
    if (!cell?.move || cell.disabled || state.completed) return false;
    move(cell.move);
    return true;
  };
  const ribbonIndex = (event) => {
    const rect = board.getBoundingClientRect?.();
    if (!rect?.width || !rect.height) return indexAt(event);
    const x = event.clientX - rect.left,
      y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return -1;
    const view = gameView(readState());
    return (
      Math.floor((y / rect.height) * (view.cells.length / view.columns)) * view.columns +
      Math.floor((x / rect.width) * view.columns)
    );
  };
  const surface = createGameDragSurface(board, ribbonIndex);
  const directIndex = surface.indexAt;
  const dropIndex = (current, event) =>
    current.game === 'gem-swap'
      ? surface.swipeIndex(current, event, gameView(readState()).cells.length)
      : directIndex(event);
  const legalTower = (state, from, to) => {
    const source = state.data.pegs[from],
      target = state.data.pegs[to];
    return (
      from !== to &&
      !!source?.length &&
      !!target &&
      (!target.length || target.at(-1) > source.at(-1))
    );
  };
  const clearDirect = (current) => {
    current?.ghost?.remove();
    for (const node of current?.trays || []) {
      delete node.dataset.dragLegal;
      delete node.dataset.dragTarget;
    }
    for (const [node, visibility] of current?.hidden || []) node.style.visibility = visibility;
    for (const node of board.querySelectorAll?.('[data-drag-legal], [data-drag-target]') || []) {
      delete node.dataset.dragLegal;
      delete node.dataset.dragTarget;
    }
  };
  const legalPour = (state, source, destination) => {
    const from = state.data.tubes[source],
      to = state.data.tubes[destination];
    return (
      source !== destination &&
      !!from?.length &&
      !!to &&
      to.length < state.data.capacity &&
      (!to.length || to.at(-1) === from.at(-1))
    );
  };
  const makeGhost = (current, state) => {
    const doc = board.ownerDocument;
    if (!doc?.createElement || !board.querySelector) return;
    const source = board.querySelector(`[data-cell="${current.index}"]`);
    let pieces;
    if (current.game === 'ball-sort') {
      const tube = state.data.tubes[current.index];
      let count = 1;
      while (
        count < tube.length &&
        tube.at(-count - 1) === tube.at(-1) &&
        state.data.revealed[current.index][tube.length - count - 1]
      )
        count++;
      pieces = [...source.querySelectorAll('.game-layer')].slice(-count);
    } else
      pieces =
        current.game === 'pebble-balance'
          ? [...source.querySelectorAll('.hanoi-disc')].slice(-1)
          : [source.querySelector('.game-piece-art')].filter(Boolean);
    const ghost = doc.createElement('div');
    ghost.className = 'game-direct-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    const origin = board.getBoundingClientRect();
    current.hidden = [];
    for (const piece of pieces) {
      const r = piece.getBoundingClientRect(),
        clone = piece.cloneNode(true);
      Object.assign(clone.style, {
        position: 'absolute',
        margin: '0',
        left: `${r.left - origin.left}px`,
        top: `${r.top - origin.top}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
        maxHeight: 'none',
        visibility: 'visible',
        transition: 'none',
      });
      ghost.append(clone);
      current.hidden.push([piece, piece.style.visibility]);
      piece.style.visibility = 'hidden';
    }
    board.append(ghost);
    current.ghost = ghost;
  };
  const directMove = (event, dx, dy) => {
    const current = gesture,
      state = readState();
    if (
      state.gameId !== current.game ||
      state.seed !== current.seed ||
      state.moves !== current.moves ||
      state.completed ||
      board.isConnected === false ||
      (current.touch && board.dataset?.immersive !== 'true')
    ) {
      suppressClick = current.moved;
      release();
      return;
    }
    if (!current.moved && Math.hypot(dx, dy) < 8) return;
    if (!current.moved) {
      current.moved = true;
      board.setPointerCapture?.(current.id);
      if (current.game !== 'soft-merge') makeGhost(current, state);
      if (current.game === 'ball-sort')
        for (const node of board.querySelectorAll?.('[data-cell]') || [])
          if (legalPour(state, current.index, Number(node.dataset.cell)))
            node.dataset.dragLegal = 'true';
    }
    event.preventDefault();
    if (current.ghost)
      current.ghost.style.transform = `translate3d(${dx}px, ${dy - (current.game === 'ball-sort' ? 12 : 0)}px, 0)`;
    const destination = dropIndex(current, event),
      columns = current.columns;
    if (current.destination === destination) return;
    if (current.target) delete current.target.dataset.dragTarget;
    current.destination = destination;
    current.target = null;
    const legal =
      destination >= 0 &&
      (current.game === 'ball-sort'
        ? legalPour(state, current.index, destination)
        : current.game === 'pebble-balance'
          ? legalTower(state, current.index, destination)
          : Math.abs(Math.floor(destination / columns) - Math.floor(current.index / columns)) +
              Math.abs((destination % columns) - (current.index % columns)) ===
            1);
    if (legal && (current.game !== 'tile-slide' || state.data.board[destination] === 0)) {
      current.target = board.querySelector?.(`[data-cell="${destination}"]`);
      if (current.target) current.target.dataset.dragTarget = 'true';
    }
  };
  const returnDirect = (current) => {
    const ghost = current.ghost,
      doc = board.ownerDocument,
      state = readState();
    if (
      !ghost ||
      current.stale ||
      state.gameId !== current.game ||
      state.seed !== current.seed ||
      state.moves !== (current.returnMoves ?? current.moves) ||
      state.completed ||
      doc?.hidden ||
      board.isConnected === false ||
      typeof ghost.animate !== 'function' ||
      globalThis.window?.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    const source = board.querySelector(`[data-cell="${current.index}"]`);
    const pieces =
      current.game === 'ball-sort'
        ? [...source.querySelectorAll('.game-layer')].slice(-ghost.children.length)
        : current.game === 'pebble-balance'
          ? [...source.querySelectorAll('.hanoi-disc')].slice(-1)
          : [source.querySelector('.game-piece-art')].filter(Boolean);
    current.hidden = pieces.map((node) => [node, node.style.visibility]);
    for (const [node] of current.hidden) node.style.visibility = 'hidden';
    board.append(ghost);
    const animation = ghost.animate(
      [{ transform: ghost.style.transform }, { transform: 'translate3d(0,0,0)' }],
      { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' },
    );
    const finish = () => {
      clearDirect(current);
      returns.delete(record);
    };
    const record = { animation, finish };
    returns.add(record);
    void animation.finished.then(finish, finish);
  };
  const commitDirect = (current, event) => {
    const state = readState();
    if (
      state.completed ||
      state.seed !== current.seed ||
      state.gameId !== current.game ||
      state.moves !== current.moves ||
      board.isConnected === false ||
      (current.touch && board.dataset?.immersive !== 'true')
    ) {
      current.stale = true;
      return;
    }
    const dx = event.clientX - current.x,
      dy = event.clientY - current.y;
    if (current.game === 'soft-merge') {
      if (Math.hypot(dx, dy) >= 24)
        move({
          type: 'option',
          index: Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 0 : 1) : dy < 0 ? 2 : 3,
        });
      return Math.hypot(dx, dy) >= 24;
    }
    const destination = dropIndex(current, event),
      view = gameView(state);
    const legal =
      current.game === 'ball-sort'
        ? legalPour(state, current.index, destination)
        : current.game === 'pebble-balance'
          ? legalTower(state, current.index, destination)
          : destination >= 0 &&
            Math.abs(
              Math.floor(destination / view.columns) - Math.floor(current.index / view.columns),
            ) +
              Math.abs((destination % view.columns) - (current.index % view.columns)) ===
              1;
    if (!legal) return;
    if (current.game === 'tile-slide') {
      if (state.data.board[destination] !== 0) return;
      board.gameDragTile = { seed: state.seed, moves: state.moves, from: current.index, dx, dy };
      try {
        applyCell(current.index);
        return readState().moves > state.moves;
      } finally {
        delete board.gameDragTile;
      }
    }
    // Selection is committed only after a legal drop. A rejected drag changes
    // neither canonical selection nor contents; existing tap-selection is respected.
    if (state.data.selected >= 0 && state.data.selected !== current.index)
      applyCell(state.data.selected);
    if (!gameView(readState()).cells[current.index]?.selected) applyCell(current.index);
    if (current.game === 'ball-sort' && current.releasePour)
      board.gameDragPour = {
        source: current.index,
        seed: state.seed,
        marbles: current.releasePour,
      };
    if (current.game === 'pebble-balance' && current.ghost?.children[0]) {
      board.gameDragHanoi = {
        from: current.index,
        seed: state.seed,
        size: String(state.data.pegs[current.index].at(-1)),
        ...current.releaseTower,
      };
    }
    if (current.game === 'gem-swap')
      board.gameDragGem = {
        seed: state.seed,
        previousMoves: readState().moves,
        source: current.index,
        destination,
      };
    try {
      applyCell(destination);
      return current.game === 'ball-sort'
        ? readState().data.tubes[current.index].length < state.data.tubes[current.index].length
        : current.game === 'pebble-balance'
          ? readState().data.transfers > state.data.transfers
          : readState().data.turns > state.data.turns;
    } finally {
      // A canonical nonmatching swap may advance moves while leaving its pieces
      // in place; only that validated attempt may return the existing ghost.
      current.returnMoves = readState().moves;
      delete board.gameDragPour;
      delete board.gameDragGem;
      delete board.gameDragHanoi;
    }
  };
  const release = () => {
    surface.flush();
    const previous = gesture;
    gesture = null;
    clearDirect(previous);
    if (board.dataset) delete board.dataset.tracing;
    if (previous && board.hasPointerCapture?.(previous.id))
      board.releasePointerCapture(previous.id);
  };
  const tracedPath = (state) =>
    state.gameId === 'flow-paths'
      ? state.data.path
      : state.data.active >= 0
        ? state.data.paths[state.data.active]
        : null;
  const stopTrace = () => {
    suppressClick = true;
    release();
  };
  const trace = (event) => {
    const state = readState();
    if (
      state.completed ||
      state.gameId !== gesture.game ||
      state.seed !== gesture.seed ||
      state.moves !== gesture.moves ||
      state.data.stage !== gesture.stage ||
      board.isConnected === false ||
      (gesture.touch && gesture.resume && board.dataset?.immersive !== 'true') ||
      tracedPath(state)?.at(-1) !== gesture.last
    )
      return stopTrace();
    const index = directIndex(event),
      columns = gameView(state).columns;
    if (index < 0 || index === gesture.last) return;
    const from = gesture.last;
    const sameRow = Math.floor(from / columns) === Math.floor(index / columns);
    const sameColumn = from % columns === index % columns;
    if (!sameRow && !sameColumn) return; // Ignore ambiguous corners; never invent a diagonal.
    const step = sameRow ? Math.sign(index - from) : Math.sign(index - from) * columns;
    for (let next = from + step; next !== index + step; next += step) {
      if (!applyCell(next)) return stopTrace();
      const current = readState();
      if (current.moves !== gesture.moves + 1) return stopTrace();
      gesture.last = next;
      gesture.moves = current.moves;
      if (current.completed || current.data.stage !== gesture.stage || !tracedPath(current)) {
        stopTrace();
        break;
      }
      if (tracedPath(current).at(-1) !== next) return stopTrace();
    }
  };
  for (const name of ['contextmenu', 'dragstart', 'selectstart'])
    listen(name, (event) => event.preventDefault());
  listen('pointerdown', (event) => {
    if (event.button !== 0 || event.isPrimary === false || gesture) return;
    suppressClick = false;
    surface.reset();
    const state = readState();
    const index = ['flow-paths', 'colour-path'].includes(state.gameId)
      ? directIndex(event)
      : indexAt(event);
    const canResume = event.pointerType !== 'touch' || board.dataset?.immersive === 'true';
    const resume =
      canResume &&
      ['flow-paths', 'colour-path'].includes(state.gameId) &&
      index >= 0 &&
      tracedPath(state)?.at(-1) === index;
    if (
      state.completed ||
      (state.gameId !== 'soft-merge' &&
        !resume &&
        board.querySelector?.(`[data-cell="${index}"]`)?.disabled)
    )
      return;
    const direct =
      directGames.has(state.gameId) &&
      (event.pointerType !== 'touch' || board.dataset?.immersive === 'true') &&
      (state.gameId === 'soft-merge' ||
        (index >= 0 && (state.gameId !== 'ball-sort' || state.data.tubes[index]?.length)));
    const ribbon =
      resume || (state.gameId === 'colour-path' && gameView(state).cells[index]?.endpoint);
    gesture = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      index,
      columns: gameView(state).columns,
      last: -1,
      game: state.gameId,
      brush:
        brushes.has(state.gameId) &&
        (event.pointerType !== 'touch' || board.dataset?.immersive === 'true'),
      touch: event.pointerType === 'touch',
      seed: state.seed,
      moves: state.moves,
      moved: false,
      ribbon,
      resume,
      direct,
      stage: state.data.stage,
    };
    // Own the sequence before a fast pointer leaves its starting cell. Capture
    // does not cancel native feed scrolling; touch pan still emits cancellation.
    if (direct || gesture.brush || ribbon) board.setPointerCapture?.(event.pointerId);
  });
  listen(
    'pointermove',
    (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (gesture.direct) {
        if (gesture.moved || Math.hypot(dx, dy) >= 8) event.preventDefault();
        surface.schedule(() => {
          if (gesture?.id === event.pointerId) directMove(event, dx, dy);
        });
        return;
      }
      if (gesture.ribbon) {
        if (!gesture.moved) {
          if (Math.hypot(dx, dy) < 8) return;
          // Current tips resume without a redundant action; labelled endpoints
          // start through canonical policy. Expanded canvases own touch drawing.
          const state = readState();
          if (
            state.seed !== gesture.seed ||
            state.moves !== gesture.moves ||
            state.gameId !== gesture.game ||
            state.completed ||
            (gesture.resume
              ? tracedPath(state)?.at(-1) !== gesture.index
              : !applyCell(gesture.index))
          ) {
            stopTrace();
            return;
          }
          gesture.moved = true;
          gesture.moves = readState().moves;
          gesture.last = gesture.index;
          board.setPointerCapture?.(gesture.id);
          if (board.dataset) board.dataset.tracing = 'true';
        }
        event.preventDefault();
        trace(event);
        return;
      }
      if (gesture.brush) {
        const state = readState();
        if (
          (gesture.touch && board.dataset?.immersive !== 'true') ||
          board.isConnected === false ||
          state.completed ||
          state.gameId !== gesture.game ||
          state.seed !== gesture.seed ||
          state.moves !== gesture.moves
        ) {
          suppressClick = gesture.moved;
          release();
          return;
        }
        if (Math.hypot(dx, dy) < 8 && !gesture.moved) return;
        if (!gesture.moved) {
          if (!applyCell(gesture.index)) return release();
          gesture.last = gesture.index;
          board.setPointerCapture?.(gesture.id);
        }
        gesture.moved = true;
        // Captured touch events retain their initial DOM target. Coordinates
        // identify the actual painted cell on an expanded canvas instead.
        const index = directIndex(event);
        if (index >= 0 && index !== gesture.last) {
          if (['mosaic-paint', 'paper-fold'].includes(gesture.game)) {
            // Sparse pointer samples still paint the cells the brush crossed.
            // Every cell remains a canonical edit with the existing Undo rules.
            const columns = gameView(state).columns,
              from = gesture.last;
            const x = from % columns,
              y = Math.floor(from / columns);
            const dx = (index % columns) - x,
              dy = Math.floor(index / columns) - y;
            const samples = Math.max(Math.abs(dx), Math.abs(dy));
            for (let step = 1; step <= samples; step++) {
              const next =
                Math.round(y + (dy * step) / samples) * columns +
                Math.round(x + (dx * step) / samples);
              if (next !== gesture.last && applyCell(next)) gesture.last = next;
              if (readState().completed) break;
            }
          } else {
            gesture.last = index;
            applyCell(index);
          }
        }
        gesture.moves = readState().moves;
        event.preventDefault();
        return;
      }
      if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) gesture = null;
    },
    { passive: false },
  );
  listen('pointerup', (event) => {
    if (!gesture || gesture.id !== event.pointerId) return;
    // Browsers may coalesce every intermediate sample on a fast drag. Resolve
    // its final position before releasing capture, including the return origin.
    if (gesture.direct) {
      surface.cancelFrame();
      directMove(event, event.clientX - gesture.x, event.clientY - gesture.y);
      if (!gesture) return;
    }
    const current = gesture;
    if (current.game === 'ball-sort' && current.ghost)
      current.releasePour = [...current.ghost.children].map((node) => ({
        rect: node.getBoundingClientRect(),
        node: node.cloneNode(true),
      }));
    if (current.game === 'pebble-balance' && current.ghost?.children[0]) {
      const disc = current.ghost.children[0];
      current.releaseTower = { rect: disc.getBoundingClientRect(), ghost: disc.cloneNode(true) };
    }
    release();
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (current.moved) {
      if (current.direct && !commitDirect(current, event)) returnDirect(current);
      suppressClick = true;
      return;
    }
    if (Math.hypot(dx, dy) < 8 && (current.brush || current.ribbon || current.direct)) {
      // Early capture retargets the native click to the board. Commit its tap
      // explicitly, then consume that click so browsers cannot apply it twice.
      const state = readState();
      if (
        state.gameId === current.game &&
        state.seed === current.seed &&
        state.moves === current.moves &&
        !state.completed &&
        current.game !== 'soft-merge'
      ) {
        applyCell(current.index);
        suppressClick = true;
      }
      return;
    }
    if (Math.abs(dx) < 24 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (current.game === 'soft-merge') {
      suppressClick = true;
      move({ type: 'option', index: dx < 0 ? 0 : 1 });
    } else if (current.game === 'gem-swap') {
      const state = readState();
      const view = gameView(state);
      const destination = current.index + (dx < 0 ? -1 : 1);
      if (
        current.index < 0 ||
        Math.floor(destination / view.columns) !== Math.floor(current.index / view.columns)
      )
        return;
      suppressClick = true;
      if (!view.cells[current.index]?.selected) applyCell(current.index);
      applyCell(destination);
    }
  });
  listen(
    'click',
    (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    },
    true,
  );
  const cancel = () => {
    const moved = gesture?.moved;
    release();
    surface.reset();
    delete board.gameDragPour;
    delete board.gameDragGem;
    for (const record of [...returns]) {
      record.animation.cancel();
      record.finish();
    }
    suppressClick = Boolean(moved);
  };
  listen('pointercancel', (event) => {
    if (gesture && event.pointerId === gesture.id) cancel();
  });
  listen('lostpointercapture', (event) => {
    if (!gesture || gesture.id !== event.pointerId) return;
    if (gesture?.moved) suppressClick = true;
    release();
  });
  listen('pointerleave', () => {
    if (gesture && !gesture.moved && !board.hasPointerCapture?.(gesture.id)) release();
  });
  listen('keydown', (event) => {
    if (event.key === 'Escape') return cancel();
    if (['colour-path', 'flow-paths'].includes(readState().gameId)) {
      if (directions[event.key] === undefined || event.altKey || event.ctrlKey || event.metaKey)
        return;
      const index = indexAt(event),
        columns = gameView(readState()).columns;
      const next = index + [-1, 1, -columns, columns][directions[event.key]];
      if (
        index < 0 ||
        next < 0 ||
        next >= gameView(readState()).cells.length ||
        (directions[event.key] < 2 && Math.floor(next / columns) !== Math.floor(index / columns))
      )
        return;
      const target = board.querySelector?.(`[data-cell="${next}"]`);
      if (target && !target.disabled) {
        event.preventDefault();
        target.focus();
      }
      return;
    }
    if (
      readState().gameId !== 'soft-merge' ||
      directions[event.key] === undefined ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey
    )
      return;
    event.preventDefault();
    move({ type: 'option', index: directions[event.key] });
  });
  const doc = board.ownerDocument;
  const hide = () => {
    if (doc.hidden) cancel();
  };
  doc?.addEventListener('visibilitychange', hide);
  const viewport = doc?.defaultView;
  viewport?.visualViewport?.addEventListener('resize', cancel);
  const expansion =
    (brushes.has(readState().gameId) ||
      directGames.has(readState().gameId) ||
      ['colour-path', 'flow-paths'].includes(readState().gameId)) &&
    typeof MutationObserver === 'function'
      ? new MutationObserver(() => {
          if (gesture?.touch && board.dataset?.immersive !== 'true') {
            const painted = gesture.moved;
            cancel();
            suppressClick = painted;
          }
        })
      : null;
  expansion?.observe(board, { attributes: true, attributeFilter: ['data-immersive'] });
  const observer =
    (['colour-path', 'flow-paths'].includes(readState().gameId) ||
      directGames.has(readState().gameId)) &&
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
          if (!entries[0]?.isIntersecting) cancel();
        })
      : null;
  observer?.observe(board);
  const unguard = guardGameSurface(board, {
    defaults: false,
    ownsTouch: () => board.dataset?.immersive === 'true' || Boolean(gesture?.ribbon),
    cancel,
  });
  const dispose = () => {
    cancel();
    unguard();
    observer?.disconnect();
    expansion?.disconnect();
    doc?.removeEventListener('visibilitychange', hide);
    viewport?.visualViewport?.removeEventListener('resize', cancel);
    for (const [name, handler, options] of listeners)
      board.removeEventListener(name, handler, options);
  };
  dispose.cancel = cancel;
  return dispose;
}
